<template>
  <!--
    ArtifactDetail SECTION — Metric artifact, "Data Source" tab.

    WHAT: Configures a metric's SQL plumbing — the DataAttribute join graph plus
      the studio-sql combined preview and the output-contract readiness check
      (show-contract). Thin wrapper over the shared JoinGraphSection; the actual
      builder is builder/StudioJoinBuilder. PLUS the contract-loop's build step:
      "Auto-generate from contract" hands the current methodology + expectation
      to the metric_build worker job, which authors the DAs, join graph,
      aggregation AND display config to satisfy them (rebuildable — the audit →
      contract-edit → rebuild loop).

    CONTRACT (props { record, artifact, context }):
      READS  record.composable_config.studio_config (steps + primary_key_columns +
             excluded_columns), record.composable_config.filters (passed as
             sql-filters), context.attributes (the DataAttribute catalog).
      EMITS  patch({ composable_config: { ...config, studio_config } }) — merges
             ONLY the join-graph keys (steps, and primary_key_columns as a
             fallback) plus excluded_columns; deliberately PRESERVES
             aggregation_type / primary_key_columns / value_conditions that the
             Aggregation tab owns (onStudio/onExcluded → emitStudio).
      POST   build-metric/ { replace? } → poll job → recompute/ → refresh().
      Also emits refresh() when a DataAttribute was edited in-place
             (@attribute-saved → $emit('refresh')).

    NOTES:
      - Auto-generate REPLACES an existing join graph (confirm() first; the
        backend also requires replace=true). Building is implicit approval of
        the contract as it stands — its hash is recorded server-side.
      - selfChrome: true in the registry — StudioJoinBuilder draws its own card.
      - No local hydrating guard here; the guard lives inside JoinGraphSection.
  -->
  <div class="space-y-3">
    <div class="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3">
      <div class="flex-1 text-[11px] text-gray-400 leading-relaxed">
        Auto-generate builds this tab plus Aggregation and Display from the current
        Expectation + Methodology. New SQL is validated against Snowflake before
        anything lands; re-run it after folding audit feedback into the contract.
      </div>
      <button
        class="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-2 hover:opacity-90 disabled:opacity-50 whitespace-nowrap bg-gray-800"
        :disabled="building"
        @click="autoGenerate"
      >
        <font-awesome-icon :icon="['fas', building ? 'circle-notch' : 'wand-magic-sparkles']" :class="building ? 'fa-spin' : ''" class="text-[10px]" />
        {{ building ? 'Building…' : 'Auto-generate from contract' }}
      </button>
    </div>
    <div
      v-if="buildMsg"
      class="text-xs rounded-lg px-3 py-2 border"
      :class="{
        'text-red-600 bg-red-50 border-red-200': buildErr,
        'text-gray-600 bg-gray-50 border-gray-200': !buildErr,
      }"
    >
      {{ buildMsg }}
    </div>

    <JoinGraphSection
      :value="studioConfig"
      :attributes="context.sources || context.attributes || []"
      :sql-url="`/api/metrics/${record.id}/studio-sql/`"
      :sql-filters="filters"
      :excluded-columns="excludedColumns"
      :contract-config="studioConfig"
      :group-by-columns="genericGroupers"
      show-contract
      :grain="studioConfig.grain || 'npi'"
      @update:value="onStudio"
      @update:excluded-columns="onExcluded"
      @update:group-by-columns="emitGroupers"
      @attribute-saved="$emit('refresh')"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { getJob } from '@/services/api/personalAnalyst';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import JoinGraphSection from './JoinGraphSection.vue';

const POLL_MS = 5000;
const MAX_POLLS = 180;

export default {
  name: 'DataSourceSection',
  components: { JoinGraphSection, FontAwesomeIcon },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch', 'refresh'],
  data() {
    return {
      building: false,
      buildMsg: '',
      buildErr: false,
      pollCount: 0,
      precomputing: false,
      precomputeMessage: '',
      precomputeError: '',
    };
  },
  computed: {
    config() { return this.record.composable_config || {}; },
    studioConfig() { return this.config.studio_config || { steps: [], primary_key_columns: ['BENE_MBI_ID'] }; },
    filters() { return this.config.filters || null; },
    excludedColumns() { return this.studioConfig.excluded_columns || []; },
    genericGroupers() { return [...((this.config.data_source_v2 || {}).group_by_columns || [])]; },
  },
  beforeUnmount() { clearTimeout(this._pollTimer); },
  methods: {
    onStudio(sc) {
      // Preserve aggregation_type / primary_key_columns / value_conditions that the
      // Aggregation section owns — merge only the join-graph keys.
      const prev = this.studioConfig;
      const merged = {
        ...prev,
        steps: sc.steps,
        // keep primary_key_columns authoritative from aggregation, but if the graph
        // introduced none yet, fall back to the graph's.
        primary_key_columns: prev.primary_key_columns || sc.primary_key_columns,
      };
      this.emitStudio(merged);
    },
    onExcluded(cols) {
      const merged = { ...this.studioConfig };
      if (cols && cols.length) merged.excluded_columns = [...cols];
      else delete merged.excluded_columns;
      this.emitStudio(merged);
    },
    emitStudio(sc) {
      this.$emit('patch', { composable_config: { ...this.config, studio_config: sc } });
    },
    emitGroupers(groupByColumns) {
      this.$emit('patch', {
        composable_config: {
          ...this.config,
          data_source_v2: {
            ...(this.config.data_source_v2 || {}),
            group_by_columns: groupByColumns,
          },
        },
      });
    },
    async precomputeGeneric() {
      this.precomputing = true;
      this.precomputeMessage = '';
      this.precomputeError = '';
      try {
        const { data } = await axios.post(
          `api/impact_analytics/composable-metrics/${this.record.id}/precompute-data-source-v2/`,
          { data_source_v2: { group_by_columns: this.genericGroupers } },
          { timeout: 600000 },
        );
        this.precomputeMessage = `${Number(data.aggregate_rows || 0).toLocaleString()} aggregate rows`;
      } catch (error) {
        this.precomputeError = error?.response?.data?.error || error.message;
      } finally {
        this.precomputing = false;
      }
    },

    async autoGenerate() {
      const hasSteps = (this.studioConfig.steps || []).length > 0;
      if (hasSteps
          // eslint-disable-next-line no-alert
          && !window.confirm('Rebuild from the contract? This REPLACES the '
                             + 'existing join graph (Aggregation and Display '
                             + 'are rewritten too).')) {
        return;
      }
      this.building = true;
      this.buildErr = false;
      this.buildMsg = 'The build agent is authoring the DAs and config from the contract — a few minutes.';
      try {
        const { data } = await axios.post(
          `api/impact_analytics/composable-metrics/${this.record.id}/build-metric/`,
          { replace: hasSteps },
        );
        if (data.status === 'FAILED') throw new Error('The build job failed to start.');
        this.pollCount = 0;
        this.schedulePoll(data.job_id);
      } catch (e) {
        this.building = false;
        this.buildErr = true;
        this.buildMsg = `Build failed: ${e?.response?.data?.error || e.message}`;
      }
    },
    async afterBuild() {
      this.buildMsg = 'Built. Precomputing (this can take a few minutes)…';
      try {
        await axios.post(
          `api/impact_analytics/composable-metrics/${this.record.id}/recompute/`,
          {}, { timeout: 600000 },
        );
        this.buildMsg = 'Built and precomputed. Review the graph below; run checks on the Audit tab.';
        this.buildErr = false;
      } catch (e) {
        this.buildErr = true;
        this.buildMsg = `Built, but precompute failed: ${e?.response?.data?.error || e.message}`;
      }
      this.building = false;
      this.$emit('refresh');
    },
    schedulePoll(jobId) {
      clearTimeout(this._pollTimer);
      this._pollTimer = setTimeout(() => this.poll(jobId), POLL_MS);
    },
    async poll(jobId) {
      this.pollCount += 1;
      try {
        const job = await getJob(jobId);
        if (job.status === 'COMPLETED') { await this.afterBuild(); return; }
        if (job.status === 'FAILED') {
          this.building = false;
          this.buildErr = true;
          this.buildMsg = `Build failed: ${job.error_message || 'the worker did not complete.'}`;
          return;
        }
      } catch (e) { /* transient poll error — keep trying */ }
      if (this.pollCount >= MAX_POLLS) {
        this.building = false;
        this.buildErr = true;
        this.buildMsg = 'The build is taking too long — check back later.';
        return;
      }
      this.schedulePoll(jobId);
    },
  },
};
</script>
